import { observer } from 'mobx-react-lite'
import { useParams } from 'react-router'
import { useEffect, useRef } from 'react'
import { Box } from 'shared/ui/Box'
import { CollapseWithIcon } from 'shared/ui/Collapse/CollapseWithIcon/CollapseWithIcon'
import { Button, Icon, Tooltip } from 'shared/ui'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { AgentFooterButtons } from 'pages/chatbot/ui/ChatbotButtons'
import { useChatbotContext } from 'pages/chatbot/context'
import { GoalActions } from 'pages/chatbot/pages/agentBookAppointment/pages/agentActions/ui/GoalActions/GoalActions'
import { useBookAppointmentBlocker } from 'pages/chatbot/pages/agentBookAppointment/hooks/useBookAppointmentBlocker'
import { StepObjectiveContent } from 'pages/chatbot/ui/ChatbotEdit/StepObjective'
import { StepQualifyContent } from 'pages/chatbot/ui/ChatbotEdit/StepQualify/ui/StepQualifyContent/StepQualifyContent'
import { StepHandoffContent } from 'pages/chatbot/ui/ChatbotEdit/StepHandoff/ui/StepHandoffContent/StepHandoffContent.tsx'
import styles from './styles.module.scss'

export const AgentActions = observer(() => {
  const { featureFlagsStore } = useEntitiesContext()
  const chatbotStore = useChatbotContext()
  const { agentId: id } = useParams()
  const {
    agentBookAppointmentStepsStore: {
      agentActionsStore,
      isExistChanges,
      confirmLeavePage,
      setAdvancedSettings,
      decreaseStep,
      agentActivateStore: { handleActivateClickWithModal },
    },
    model: { isActive },
    fieldsControl,
    stepQualifyStore,
    errorObjectiveQualifyInstruction,
    errorHandoff,
    qualifiedActions,
    disqualifiedActions,
    validateTrigger,
  } = chatbotStore
  const {
    isOpened,
    onToggle,
    successActions,
    fallbackActions,
    handleNextClick,
    handleSaveDraft,
    successActionsErrors,
    fallbackActionsErrors,
    qualifiedActionsErrors,
    disqualifiedActionsErrors,
    dataCollectionError,
    dataCollectionErrorAlert,
    disabled,
  } = agentActionsStore

  useBookAppointmentBlocker({
    isExistChanges,
    confirmLeavePage: (blocker) =>
      confirmLeavePage(
        blocker,
        id,
        isActive
          ? async (id) => {
              return await handleActivateClickWithModal(id, {
                isPublished: isActive,
                shouldRedirect: false,
              })
            }
          : handleSaveDraft
      ),
  })

  const successError = successActionsErrors.getError('success_actions')
  const isSuccessOwnError = successActionsErrors.hasOwnError('success_actions')
  const isSuccessNestedError = successActionsErrors.hasError('success_actions')

  const fallbackError = fallbackActionsErrors.getError('fallback_actions')
  const isFallbackOwnError = fallbackActionsErrors.hasOwnError('fallback_actions')
  const isFallbackNestedError = fallbackActionsErrors.hasError('fallback_actions')

  const isQualifiedOwnError = qualifiedActionsErrors.hasOwnError('qualified_actions')
  const isDisqualifiedOwnError = disqualifiedActionsErrors.hasOwnError('disqualified_actions')

  useEffect(() => {
    if (!!successActions.list.length && isSuccessOwnError)
      successActionsErrors.removeError('success_actions')

    if (!!fallbackActions.list.length && isFallbackOwnError)
      fallbackActionsErrors.removeError('fallback_actions')

    if (!!qualifiedActions.list.length && isQualifiedOwnError)
      qualifiedActionsErrors.removeError('qualified_actions')

    if (!!disqualifiedActions.list.length && isDisqualifiedOwnError)
      disqualifiedActionsErrors.removeError('disqualified_actions')
  }, [
    successActions.list,
    fallbackActions.list,
    qualifiedActions.list,
    disqualifiedActions.list,
    isSuccessOwnError,
    isFallbackOwnError,
    isQualifiedOwnError,
    isDisqualifiedOwnError,
  ])

  const refBody = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!validateTrigger || !refBody.current) return

    const firstError = refBody.current.querySelector('[data-error]:not([data-error=""])')
    if (!(firstError instanceof HTMLElement)) return

    const findScrollContainer = (element: HTMLElement | null): HTMLElement | null => {
      if (!element) return null
      const { overflowY } = getComputedStyle(element)
      return overflowY === 'scroll' || overflowY === 'auto'
        ? element
        : findScrollContainer(element.parentElement)
    }

    const scrollContainer = findScrollContainer(firstError.parentElement)
    if (!scrollContainer) return

    scrollContainer.scrollTo({
      top:
        scrollContainer.scrollTop +
        firstError.getBoundingClientRect().top -
        scrollContainer.getBoundingClientRect().top,
      behavior: 'smooth',
    })
  }, [validateTrigger])

  useEffect(() => {
    fieldsControl.init()
    stepQualifyStore.initDefault()

    return () => {
      stepQualifyStore.dispose()
    }
  }, [])

  const goalMetError = isSuccessOwnError
    ? successError
    : isSuccessNestedError
    ? 'Please complete all the fields'
    : ''

  const goalNotMetError = isFallbackOwnError
    ? fallbackError
    : isFallbackNestedError
    ? 'Please complete all the fields'
    : ''

  const tooltipText = [
    'Success and fallback conditions are set in the prompt. Customize them in',
    <Button
      key='advanced-settings'
      text='Advanced settings'
      typeBtn='link'
      contained='secondary'
      paddingInline={0}
      className={styles.tooltipButton}
      onClick={(e) => {
        e.preventDefault()
        setAdvancedSettings()
      }}
    />,
  ]

  const tooltipWithLink = (
    <Tooltip label={tooltipText} placement='top' breakWord type='description'>
      <Icon
        icon='question'
        fontSize={14}
        color='var(--content-neutral-primary)'
        padding={'0 2px'}
      />
    </Tooltip>
  )

  return (
    <Box display={'flex'} flexDirection={'column'} height={'100%'} justifyContent={'space-between'}>
      <div ref={refBody} className={styles.collapseActionsWrapper}>
        <div data-error={dataCollectionError}>
          <CollapseWithIcon
            opened={isOpened('dataCollection')}
            onClick={() => onToggle('dataCollection')}
            icon={'barChart'}
            title={'Collect data'}
            label={'Gather specific details like team size, budget, and more'}
            headerClassName={styles.collapseHeader}
            variant={'active'}
            className={styles.dataCollectionCollapse}
            error={dataCollectionError}
          >
            <StepObjectiveContent
              type={'booking'}
              errorAlert={dataCollectionErrorAlert}
              error={dataCollectionError}
            />
          </CollapseWithIcon>
        </div>

        {featureFlagsStore.qualify && (
          <div data-error={errorObjectiveQualifyInstruction}>
            <CollapseWithIcon
              opened={isOpened('qualification')}
              onClick={() => onToggle('qualification')}
              icon={'usersCheck'}
              title={'Qualification'}
              label={'Agent will qualify contacts before booking a meeting'}
              headerClassName={styles.collapseHeader}
              variant={'active'}
              className={styles.collapseContent}
              error={errorObjectiveQualifyInstruction}
            >
              <StepQualifyContent type={'booking'} />
            </CollapseWithIcon>
          </div>
        )}
        <div data-error={errorHandoff}>
          <CollapseWithIcon
            opened={isOpened('handoff')}
            onClick={() => onToggle('handoff')}
            icon={'userRight'}
            title={'Human handoff'}
            label={'Hand off the conversation if the AI Agent can’t help'}
            headerClassName={styles.collapseHeader}
            variant={'active'}
            className={styles.collapseContent}
            error={errorHandoff}
          >
            <StepHandoffContent type={'booking'} />
          </CollapseWithIcon>
        </div>
        <div data-error={goalMetError}>
          <CollapseWithIcon
            opened={isOpened('goalMet')}
            onClick={() => onToggle('goalMet')}
            icon={'target'}
            title={'Goal met'}
            label={"Set the agent's success actions"}
            headerClassName={styles.collapseHeader}
            variant={'active'}
            className={styles.collapseContent}
            error={goalMetError}
            subtitle={tooltipWithLink}
          >
            <GoalActions actions={successActions} />
          </CollapseWithIcon>
        </div>
        <div data-error={goalNotMetError}>
          <CollapseWithIcon
            opened={isOpened('goalNotMet')}
            onClick={() => onToggle('goalNotMet')}
            icon={'cancelFilled'}
            title={'Goal not met'}
            label={'Define what happens when the goal is not meet'}
            headerClassName={styles.collapseHeader}
            variant={'active'}
            className={styles.collapseContent}
            error={goalNotMetError}
            subtitle={tooltipWithLink}
          >
            <GoalActions actions={fallbackActions} />
          </CollapseWithIcon>
        </div>
      </div>
      <AgentFooterButtons
        decreaseStep={decreaseStep}
        handleNextClick={() => handleNextClick(id)}
        className={styles.footerButtons}
        isNextButtonDisabled={disabled}
      />
    </Box>
  )
})
